import React, { useState, useMemo } from 'react'
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Box,
  useMediaQuery,
  Grid
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import MenuIcon from '@mui/icons-material/Menu'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import EditIcon from '@mui/icons-material/Edit'
import QuizIcon from '@mui/icons-material/Quiz'
import LogoutIcon from '@mui/icons-material/Logout'
import AppIcon from './AppIcon'
import NotificationsIcon from './NotificationsIcon'
import { TextHover, TextSemiBold } from './TextStyling'
import { logoutUser } from '../actions/loginActions'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getUserDetails } from '../utils/LocalStorage'
import { paddingHorizontal } from '../utils/AppConstants'
import { ContactSupport } from '@mui/icons-material'

export default function HeaderEmployer () {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = getUserDetails()

  const headerItems = useMemo(() => {
    const base = [{ label: 'Dashboard', nav: '/employer/dashboard' }]
    if (user.isSubEmployer) {
      base.push(
        { label: 'Manage jobs', nav: '/employer/managejob' },
        { label: 'Post a new job', nav: '/employer/postnewjob' }
      )
    } else {
      base.push(
        { label: 'Manage users', nav: '/employer/manageusers' },
        {
          label: 'Jobs',
          menu: [
            { label: 'Manage jobs', nav: '/employer/managejob' },
            { label: 'Post a new job', nav: '/employer/postnewjob' }
          ]
        },
        { label: 'Transactions', nav: '/employer/transactions' }
      )
    }
    return base
  }, [user.isSubEmployer])

  const [drawerOpen, setDrawerOpen] = useState(false)

  const [anchorProfile, setAnchorProfile] = useState(null)
  const openProfile = Boolean(anchorProfile)
  const handleProfileOpen = e => setAnchorProfile(e.currentTarget)
  const handleProfileClose = () => setAnchorProfile(null)
  const onLogout = () => {
    handleProfileClose()
    dispatch(logoutUser(navigate, true))
  }

  const [anchorJobs, setAnchorJobs] = useState(null)
  const openJobs = Boolean(anchorJobs)
  const handleJobsOpen = e => setAnchorJobs(e.currentTarget)
  const handleJobsClose = () => setAnchorJobs(null)

  const ProfilePicture = () =>
    user.profilePicture ? <Avatar src={user.profilePicture} /> : <Avatar />

  return (
    <>
      <AppBar
        position='static'
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.border.main}`
        }}
      >
        <Toolbar
          sx={{
            px: paddingHorizontal,
            height: 55,
            p: '0 0',
            ml: '10px',
            mr: '10px'
          }}
        >
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <AppIcon sx={{ width: 100 }} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {!isMobile &&
              headerItems.map((item, idx) =>
                item.menu ? (
                  <React.Fragment
                    key={idx}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <TextSemiBold
                      onClick={handleJobsOpen}
                      aria-controls={openJobs ? 'jobs-menu' : undefined}
                      aria-haspopup='true'
                      aria-expanded={openJobs ? 'true' : undefined}
                      sx={{
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                    >
                      <TextHover
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        {item.label}
                        <KeyboardArrowDownIcon sx={{ width: '20px' }} />
                      </TextHover>
                    </TextSemiBold>
                    <Menu
                      id='jobs-menu'
                      anchorEl={anchorJobs}
                      open={openJobs}
                      onClose={handleJobsClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      PaperProps={{
                        elevation: 0,
                        sx: {
                          mt: 1,
                          pt: 0,
                          borderTop: `4px solid ${theme.palette.primary.main}`,
                          boxShadow: '0px 4px 12px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      {item.menu.map((sub, i) => (
                        <MenuItem
                          key={i}
                          onClick={() => {
                            handleJobsClose()
                            navigate(sub.nav)
                          }}
                        >
                          {sub.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </React.Fragment>
                ) : (
                  <TextSemiBold
                    key={idx}
                    onClick={() => navigate(item.nav)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TextHover>{item.label}</TextHover>
                  </TextSemiBold>
                )
              )}

            {!user.isSubEmployer && !isMobile && (
              <NotificationsIcon usertype={user.usertype} />
            )}

            <Box
              onClick={handleProfileOpen}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <ProfilePicture />
              <TextSemiBold sx={{ ml: 1, fontSize: 14 }}>
                {user.firstname} {user.lastname}
              </TextSemiBold>
            </Box>

            <Menu
              anchorEl={anchorProfile}
              open={openProfile}
              onClose={handleProfileClose}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              sx={{ mt: 1 }}
            >
              <MenuItem disabled>
                <ListItemIcon>
                  <ProfilePicture />
                </ListItemIcon>
                <Box sx={{ ml: 1 }}>
                  <Grid>
                    <TextSemiBold>
                      {user.firstname} {user.lastname}
                    </TextSemiBold>
                  </Grid>
                  <TextSemiBold variant='body2' color='text.secondary'>
                    {user.companyname}
                  </TextSemiBold>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleProfileClose()
                  navigate('/employer/editprofile')
                }}
              >
                <ListItemIcon>
                  <EditIcon fontSize='small' />
                </ListItemIcon>
                Edit Profile
              </MenuItem>
              {/* <MenuItem
                onClick={() => {
                  handleProfileClose()
                  navigate('https://www.virtualrecruiters.co/contact-us/')
                }}
              >
                <ListItemIcon>
                  <ContactSupport fontSize='small' />
                </ListItemIcon>
                Contact us
              </MenuItem> */}
              <MenuItem onClick={() => window.open('/faq', '_blank')}>
                <ListItemIcon>
                  <QuizIcon fontSize='small' />
                </ListItemIcon>
                FAQ
              </MenuItem>
              <MenuItem onClick={onLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize='small' />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
            <Grid sx={{ display: 'flex', alignItems: 'center' }}>
              <Grid>
                {!user.isSubEmployer && isMobile && (
                  <NotificationsIcon usertype={user.usertype} />
                )}
              </Grid>
              <Grid>
                {isMobile && (
                  <IconButton
                    edge='end'
                    onClick={() => setDrawerOpen(true)}
                    // sx={{
                    //   ml: 2
                    // }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor='right'
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box
          sx={{ width: 250 }}
          role='presentation'
          onClick={() => setDrawerOpen(false)}
        >
          <List>
            {headerItems.map((item, idx) => (
              <React.Fragment key={idx}>
                {item.menu ? (
                  <>
                    <ListItemText sx={{ px: 2, pt: 2, fontWeight: 'bold' }}>
                      {item.label}
                    </ListItemText>
                    {item.menu.map((sub, i) => (
                      <ListItemButton
                        key={i}
                        sx={{ pl: 4 }}
                        onClick={() => navigate(sub.nav)}
                      >
                        <ListItemText primary={sub.label} />
                      </ListItemButton>
                    ))}
                    <Divider />
                  </>
                ) : (
                  <ListItemButton onClick={() => navigate(item.nav)}>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  )
}
